import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Trash2, X, Loader2, Pencil, Search, AlertTriangle } from "lucide-react";
import adminApi from "./adminApi";

const emptyForm = {
  seo_pageid: "",
  seo_metatag: "",
  seo_metatitle: "",
  seo_metadesc: "",
  seo_keyword: "",
};

export default function ManageSeo() {
  const [pages, setPages] = useState([]); // flat, ordered, with level 0/1/2 + seo (or null)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // seo_pageid being edited (always an existing page)
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchPages = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/seo");
      setPages(res.data.pages || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load pages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  // Only pages that already have an SEO record are selectable for delete —
  // there's nothing to delete for a page that was never given SEO details.
  const deletablePageIds = useMemo(
    () => pages.filter((p) => p.seo).map((p) => p.page_id),
    [pages]
  );

  // ── Selection ────────────────────────────────────────────────────────────
  const allSelected = deletablePageIds.length > 0 && selected.length === deletablePageIds.length;
  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : deletablePageIds);
  };
  const toggleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ── Delete (single or multiple, via checkbox) ───────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected SEO record(s)? This cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    try {
      await adminApi.post("/admin/seo/bulk-delete", { ids: selected });
      showToast("success", `${selected.length} record(s) deleted.`);
      setSelected([]);
      fetchPages();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete selected record(s).");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Edit modal — click a page row to set/update its SEO details ────────
  const openEditModal = (page) => {
    setEditingId(page.page_id);
    setForm({
      seo_pageid: String(page.page_id),
      seo_metatag: page.seo?.seo_metatag || "",
      seo_metatitle: page.seo?.seo_metatitle || "",
      seo_metadesc: page.seo?.seo_metadesc || "",
      seo_keyword: page.seo?.seo_keyword || "",
    });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.seo_metatitle.trim()) {
      setFormError("Meta Title is required.");
      return;
    }

    setSaving(true);
    try {
      // Single endpoint upserts by seo_pageid — updates if a record already
      // exists for this page, otherwise inserts a new one.
      await adminApi.post("/admin/seo", form);
      showToast("success", "SEO details saved.");
      setShowModal(false);
      fetchPages();
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage SEO
          </h3>
        </div>
        <AnimatePresence>
          {selected.length > 0 && (
            <motion.button
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
            >
              {bulkDeleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
              Delete Selected ({selected.length})
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading pages...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : pages.length === 0 ? (
          <div className="py-16 text-center">
            <Search size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No pages found.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
           <table className="w-full text-sm border-collapse">
             <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-sm">
                  <th className="th-colorful pl-3 pr-2 py-1.5 w-8 border-b-2 border-r normal-case">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                    />
                  </th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Page Name</th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Meta Title</th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2">Keyword</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {pages.map((p, i) => (
                    <motion.tr
                      key={p.page_id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      onClick={() => openEditModal(p)}
                      className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                        selected.includes(p.page_id) ? "bg-[#0EA5A4]/15" : ""
                      }`}
                    >
                      <td className="pl-3 pr-2 py-2 border-r border-slate-200" onClick={(e) => e.stopPropagation()}>
                        {p.seo && (
                          <input
                            type="checkbox"
                            checked={selected.includes(p.page_id)}
                            onChange={() => toggleSelectOne(p.page_id)}
                            className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                          />
                        )}
                      </td>
                      <td
                          className={`px-3 py-2 text-slate-800 border-r border-slate-200 ${p.level === 0 ? "font-semibold" : "font-normal"}`}
                          style={{ paddingLeft: `${12 + p.level * 24}px` }}
                        >
                        {p.level > 0 && <span className="text-slate-300 mr-1">--</span>}
                        {p.page_name}
                      </td>
                      {p.seo ? (
                        <>
                          <td className="px-3 py-2 text-slate-600 max-w-[260px] truncate border-r border-slate-200" title={p.seo.seo_metatitle}>
                            {p.seo.seo_metatitle || "—"}
                          </td>
                          <td className="px-3 py-2 text-slate-500 max-w-[220px] truncate" title={p.seo.seo_keyword}>
                            {p.seo.seo_keyword || "—"}
                          </td>
                        </>
                      ) : (
                        <td colSpan={2} className="px-3 py-2 text-red-500 text-xs">
                          <span className="inline-flex items-center gap-1.5">
                            <AlertTriangle size={12} /> SEO Details Not Added
                          </span>
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">Edit SEO Details</h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Page Name</label>
                  <input
                    type="text"
                    value={pages.find((p) => p.page_id === editingId)?.page_name || ""}
                    disabled
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 text-slate-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Meta Tag</label>
                  <input
                    type="text"
                    value={form.seo_metatag}
                    onChange={(e) => setForm({ ...form, seo_metatag: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Meta Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.seo_metatitle}
                    onChange={(e) => setForm({ ...form, seo_metatitle: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Meta Description</label>
                  <textarea
                    value={form.seo_metadesc}
                    onChange={(e) => setForm({ ...form, seo_metadesc: e.target.value })}
                    rows={3}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Keyword</label>
                  <textarea
                    value={form.seo_keyword}
                    onChange={(e) => setForm({ ...form, seo_keyword: e.target.value })}
                    rows={2}
                    placeholder="comma, separated, keywords"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                  />
                </div>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={15} />}
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
